import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import { pageHasUnsavedEdits, useUndo } from '@/contexts/UndoContext';
import { canEdit, hasPermission } from '@/lib/permissions';
import {
  Bot, Clock, DraftingCompass, Files, FolderKanban, History, LayoutDashboard, ListChecks, LogOut, Package,
  PanelLeft, PanelLeftClose, Plus, ReceiptText, Settings, ShoppingCart, TrendingUp, UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { BreadcrumbProvider, Breadcrumbs } from '@/components/ui/breadcrumbs';
import { CommandPalette, CommandPaletteTrigger, useCommandPalette } from '@/components/ui/command-palette';
import { NotificationsBell } from '@/components/ui/notifications-bell';
import { HelpMenu } from '@/components/entry/HelpMenu';
import { PasskeyOffer } from '@/components/entry/PasskeyOffer';
import { UserMenu, useAccountActions } from '@/components/entry/UserMenu';
import { MoreSheet, PhoneTabBar, SidebarNav } from '@/components/entry/ShellNav';
import { useNavCounts } from '@/components/entry/useNavCounts';
import { rememberReturnTo, sessionLengthText, writeSessionNotice } from '@/components/entry/session';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { withoutLeaveGuard } from '@/hooks/useUnsavedChanges';
import { NAV_GROUPS } from '@/lib/nav';
import { cn } from '@/lib/utils';

// Labels, routes and permissions are unchanged; the sidebar groups them (lib/nav: Workspace / Delivery /
// Supply / Finance / Tools — `section` is also the first top-bar crumb) and every item has its own icon.
const NAV_ITEMS = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard', section: 'Workspace' },
  { path: '/projects', icon: FolderKanban, label: 'Projects', permission: 'projects', section: 'Delivery' },
  { path: '/tasks', icon: ListChecks, label: 'Tasks', permission: 'tasks', section: 'Workspace' },
  { path: '/shop-drawings', icon: DraftingCompass, label: 'Shop Drawings', permission: 'shop_drawings', section: 'Delivery' },
  { path: '/documents', icon: Files, label: 'Documents', permission: 'files', section: 'Delivery' },
  { path: '/inventory', icon: Package, label: 'Inventory', permission: 'inventory', section: 'Supply' },
  { path: '/procurement', icon: ShoppingCart, label: 'Procurement', permission: 'procurement', section: 'Supply' },
  { path: '/forecast', icon: TrendingUp, label: 'Forecast', permission: 'forecast', section: 'Finance' },
  { path: '/invoicing', icon: ReceiptText, label: 'Invoicing', permission: 'invoicing', section: 'Finance' },
  { path: '/ai', icon: Bot, label: 'AI', permission: 'ai', section: 'Tools' },
  { path: '/admin', icon: Settings, label: 'Admin', permission: 'admin', section: 'Tools' },
];

const SECTION_LABELS = new Set(NAV_GROUPS.map((group) => group.label));
const PHONE_QUERY = '(max-width: 767px)';
const PHONE_TABBAR_HEIGHT = 'calc(64px + env(safe-area-inset-bottom, 0px))';

/**
 * Route crumbs + what the page added (useBreadcrumbs): "Supply / Inventory / Catalog".
 * A project page has no section: "Projects / LIV Waterside / Stages" (Projects links back to the list).
 */
function routeCrumbs(pathname, trail) {
  const extra = trail || [];
  if (/^\/projects\/[^/]+/.test(pathname)) return [{ label: 'Projects', to: '/projects' }, ...extra];
  const item = NAV_ITEMS.find((entry) => (entry.path === '/' ? pathname === '/' : pathname.startsWith(entry.path)));
  if (!item) return extra;
  return [item.section, extra.length ? { label: item.label, to: item.path } : item.label, ...extra];
}

/** "LIV Waterside › Stages", "Inventory › Catalog", "Dashboard" — the page's name for the sign-in notice. */
function crumbsLabel(crumbs) {
  const labels = crumbs
    .map((item) => (typeof item === 'string' ? item : item?.label))
    .filter(Boolean)
    .filter((label, index) => !(index === 0 && SECTION_LABELS.has(label)));
  const meaningful = labels.length > 1 && labels[0] === 'Projects' ? labels.slice(1) : labels;
  return meaningful.slice(-2).join(' › ');
}

function BrandMark({ collapsed }) {
  return (
    <img
      src="/sara-eng-logo.svg"
      alt=""
      className={cn('object-contain', collapsed ? 'h-8 w-8' : 'h-[30px] w-auto max-w-[150px]')}
      data-testid="brand-logo"
    />
  );
}

export function Layout({ children }) {
  const { user, logout, sessionExpired, sessionTimes } = useAuth();
  const { appName } = useAppSettings();
  const { registerConfirm, undoEpoch } = useUndo();
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [moreOpen, setMoreOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [crumbTrail, setCrumbTrail] = useState(null);
  const [expiredStay, setExpiredStay] = useState(false);

  // Undo confirmations (UndoContext lives outside the router) use the app's ConfirmDialog.
  useEffect(() => registerConfirm(confirm), [registerConfirm, confirm]);

  const filteredNav = useMemo(() => NAV_ITEMS.filter((item) => hasPermission(user, item.permission)), [user]);
  const counts = useNavCounts(Boolean(user), undoEpoch);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const account = useAccountActions({
    user,
    onSignOut: handleLogout,
    modules: filteredNav.map((item) => item.label),
  });

  // ⌘K / Ctrl+K search (02 #9): every page this role may open, plus a few quick actions. The server decides
  // which records each role may find (GET /api/search).
  const palette = useCommandPalette({ enabled: Boolean(user) });
  // Same rule as the Dashboard shortcut and ProjectsPage ?new=1 (BUG-002): hidden for Viewer accounts and for a role
  // whose Projects level is View only in Admin › Access.
  const canCreateProject = canEdit(user, 'projects');
  const palettePages = useMemo(
    () => filteredNav.map(({ label, path, icon }) => ({ label, path, icon, keywords: [label.toLowerCase()] })),
    [filteredNav],
  );
  const { openRecent, openProfile, signOut } = account;
  const paletteActions = useMemo(() => [
    { id: 'new-project', label: 'New project', icon: Plus, to: '/projects?new=1', keywords: ['create', 'add'], hidden: !canCreateProject },
    { id: 'recent-changes', label: 'Recent changes', icon: History, onSelect: openRecent, keywords: ['undo', 'history'], hint: 'Undo' },
    { id: 'profile', label: 'My profile', icon: UserRound, onSelect: openProfile, keywords: ['account', 'passkey', 'session'] },
    { id: 'sign-out', label: 'Sign out', icon: LogOut, onSelect: signOut, keywords: ['log out', 'logout'] },
  ], [canCreateProject, openRecent, openProfile, signOut]);

  const crumbs = routeCrumbs(location.pathname, crumbTrail);
  const pageLabel = crumbsLabel(crumbs);
  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  // Remember which page this tab is on: if the server ends the session (lib/api.js reloads on /login after
  // a 401), the sign-in page can still bring the user back here.
  useEffect(() => {
    if (!user || sessionExpired) return;
    rememberReturnTo({ path: currentPath, label: pageLabel, times: sessionTimes });
  }, [user, sessionExpired, currentPath, pageLabel, sessionTimes]);

  // Session ran out (AuthContext): go to sign-in, which brings the user straight back here afterwards.
  const goSignIn = useCallback(() => {
    writeSessionNotice({ reason: 'expired', path: currentPath, label: pageLabel, times: sessionTimes });
    withoutLeaveGuard(() => navigate(
      `/login?next=${encodeURIComponent(currentPath)}&reason=expired`,
      { replace: true },
    ));
  }, [currentPath, navigate, pageLabel, sessionTimes]);
  const goSignInRef = useRef(goSignIn);
  goSignInRef.current = goSignIn;

  useEffect(() => {
    if (!sessionExpired) {
      setExpiredStay(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      // Nothing to lose: straight to sign-in. Unsaved edits can no longer be saved, but the user may
      // want to copy them first — ask instead of throwing them away.
      if (!pageHasUnsavedEdits()) {
        goSignInRef.current();
        return;
      }
      const ok = await confirm({
        title: 'Your session has ended',
        description: `You were signed in for ${sessionLengthText(sessionTimes)}. Changes on this page can no longer be saved. Copy anything you need, then sign in again — you will come back to this page.`,
        confirmLabel: 'Sign in again',
        cancelLabel: 'Copy my edits first',
        icon: Clock,
      });
      if (cancelled) return;
      if (ok) goSignInRef.current();
      else setExpiredStay(true);
    })();
    return () => { cancelled = true; };
  }, [sessionExpired, confirm, sessionTimes]);

  // The phone tab bar sits over the bottom of the page: floating bars and toasts rise above it.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    if (isPhone) root.style.setProperty('--app-bottom-nav-height', PHONE_TABBAR_HEIGHT);
    else root.style.removeProperty('--app-bottom-nav-height');
    return () => root.style.removeProperty('--app-bottom-nav-height');
  }, [isPhone]);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  return (
    <div className="app-theme-background min-h-[100dvh] md:flex" data-testid="main-layout">
      {/* Desktop sidebar: grouped navigation, counts, the user at the bottom. */}
      <aside
        className={cn(
          'app-theme-sidebar sticky top-0 z-40 hidden h-[100dvh] shrink-0 flex-col border-r border-white/10 md:flex ui-transition',
          collapsed ? 'w-[72px]' : 'w-[248px]'
        )}
      >
        <div className={cn('flex h-16 shrink-0 items-center gap-2', collapsed ? 'flex-col justify-center' : 'justify-between pl-5 pr-3')}>
          {!collapsed ? <BrandMark collapsed={false} /> : null}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setCollapsed((v) => !v)}
            className="shrink-0 text-[color:var(--app-sidebar-muted)] hover:bg-white/10 hover:text-white"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <PanelLeft className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto pb-3">
          <SidebarNav items={filteredNav} pathname={location.pathname} collapsed={collapsed} counts={counts} />
        </div>
        <div className={cn('border-t border-white/10', collapsed ? 'p-2' : 'p-3')}>
          <UserMenu user={user} onLogout={handleLogout} variant="sidebar" collapsed={collapsed} account={account} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="app-theme-header sticky top-0 z-30 border-b border-border/80 backdrop-blur-md supports-[backdrop-filter]:bg-[color-mix(in_srgb,var(--app-header-background)_85%,transparent)]">
          <div className="flex h-[var(--app-header-height)] items-center justify-between gap-3 px-4 md:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-2">
              <p className="min-w-0 truncate font-heading text-[15px] font-bold md:hidden">{appName}</p>
              <Breadcrumbs items={crumbs} className="hidden md:block" />
            </div>
            {/* No permanent Undo button: changes offer Undo in a toast; older ones are under
                "Recent changes" in the user menu (sidebar bottom / phone "More").
                02 #9: search everything (⌘K) and the notifications bell. */}
            <div className="flex shrink-0 items-center gap-1 md:gap-2" data-testid="topbar-tools">
              <CommandPaletteTrigger onClick={palette.show} className="lg:w-[340px] md:w-[260px]" />
              <NotificationsBell />
              <HelpMenu onSearch={palette.show} className="hidden md:grid" />
            </div>
          </div>
        </header>

        {expiredStay ? (
          <div
            role="alert"
            className="sticky top-[var(--app-header-height)] z-20 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[color:var(--brand-soft-border)] bg-brand-soft px-4 py-2.5 text-[13px] text-ink md:px-6 lg:px-8"
            data-testid="session-ended-bar"
          >
            <Clock className="h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
            <p className="min-w-0 flex-1">
              <b className="font-bold">Your session has ended.</b> Changes here can no longer be saved — copy what you need, then sign in again.
            </p>
            <Button type="button" size="sm" onClick={goSignIn} data-testid="session-ended-sign-in">Sign in again</Button>
          </div>
        ) : null}

        <main className="flex-1 pb-[var(--app-bottom-nav-height,0px)] md:pb-0">
          {/* Re-mounts the page after an undo so it refetches — no full browser reload. Unsaved edits are
              not lost silently: UndoContext asks the page's useUnsavedChanges guard (via a synthetic
              beforeunload, as a reload would) and confirms "Discard your unsaved edits" first. */}
          <div key={undoEpoch} className="mx-auto max-w-[1600px] px-4 py-6 md:px-6 md:py-8 lg:px-8 lg:py-10">
            <BreadcrumbProvider onChange={setCrumbTrail}>{children}</BreadcrumbProvider>
          </div>
        </main>
      </div>

      {/* Phone: bottom tab bar + "More" sheet (all pages and the account). */}
      <PhoneTabBar
        items={filteredNav}
        pathname={location.pathname}
        counts={counts}
        onMore={() => setMoreOpen(true)}
        moreOpen={moreOpen}
      />
      <MoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        items={filteredNav}
        pathname={location.pathname}
        counts={counts}
        user={user}
        account={account}
      />
      {account.dialogs}
      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} pages={palettePages} actions={paletteActions} />
      {/* 01 #9: once, right after a password sign-in on a phone — "Use Face ID next time?" */}
      <PasskeyOffer />
    </div>
  );
}
